/**
 * b2b-plus-quick-add.js
 *
 * Defines the `<b2b-plus-quick-add>` custom element.
 *
 * Renders a search field on the cart page. As the user types, a dropdown of
 * matching products is fetched by resolving Shopify predictive search
 * (`GET /search/suggest.json`) into catalog-priced product JSON. Selecting a
 * product adds it to the B2B cart without quantities (inventory shell only; same
 * resolution as bulk-add) and prepends the product table to the cart list
 * without a full page reload.
 *
 * Uses a **closed** shadow root so internal markup and styles are fully
 * encapsulated. Styles are loaded from `b2b-plus-quick-add.css` via a
 * `<link>` tag injected into the shadow root; the URL is supplied through the
 * `data-stylesheet` attribute on the host element. CSS custom properties
 * (`--b2b-plus-*`, `--b2b-plus-*`, etc.) from the host cascade into the shadow root as usual.
 *
 * Depends on:
 *   - `b2b-plus-utils.js`  — `getProductJSON`, `getInventory`, `getProductRecord`,
 *                             `toastNotification`
 *   - `window.b2bPlus.strings` — all user-facing labels (quickAdd*)
 */

import { render, html, nothing } from 'lit-html';
import {
  quickAddShellTemplate,
  quickAddProductRowTemplate,
  quickAddStatusRowTemplate,
} from 'b2b-plus/ui-templates';
import {
  getInventory,
} from 'b2b-plus/inventory';
import {
  toastNotification,
} from 'b2b-plus/ui';
import {
  productHandleInCart,
  getProductJSON,
  addFirstAvailableB2BLine,
} from 'b2b-plus/utils';

// ── Module-level constants ────────────────────────────────────────────────────

/** Minimum number of characters before a search request is sent. */
const SEARCH_MIN_QUERY_LENGTH = 2;

/** Milliseconds to wait after the last keystroke before sending the search request. */
const SEARCH_INPUT_DEBOUNCE_MS = 300;

/**
 * Milliseconds to delay closing the results panel after the input loses focus.
 * This gap lets a `click` on a result row fire before the panel disappears.
 */
const RESULTS_PANEL_BLUR_CLOSE_DELAY_MS = 150;

// ─── Quick-add API ───────────────────────────────────────────────────────────

/** Products returned per search. */
const SEARCH_RESULT_LIMIT = 10;

/**
 * Catalog-priced product JSON for a search query, in two steps:
 *
 *   1. `GET /search/suggest.json` — Shopify's predictive search, used purely to turn
 *      the typed query into a list of matching product IDS.
 *   2. `getProductJSON()` — the catalog-price API, which resolves those ids into
 *      buyer-priced product JSON.
 *
 * The old single-request path rendered `sections/b2b-plus-product-data.liquid` with
 * `?section_id=`; that section is gone along with the rest of the handle-based
 * product-data plumbing.
 *
 * @param {string} searchQuery
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object[]>} Normalised product objects (same shape as getProductJSON).
 */
const getProductJSONFromStorefrontSearch = async (searchQuery, options = {}) => {
  const q = String(searchQuery ?? '').trim();
  if (!q) return [];

  const params = new URLSearchParams({
    q,
    'resources[type]':  'product',
    'resources[limit]': String(SEARCH_RESULT_LIMIT),
  });
  const searchPath = String(window.b2bPlus.config.searchUrl || '/search').replace(/\/$/, '') || '/search';
  const res = await fetch(`${searchPath}/suggest.json?${params.toString()}`, { signal: options.signal });
  if (!res.ok) throw new Error(`b2b-plus: product search failed (HTTP ${res.status})`);

  const suggestions = (await res.json())?.resources?.results?.products ?? [];
  const lookups = suggestions
    .map((p) => ({ handle: String(p?.handle ?? ''), productId: p?.id }))
    .filter((l) => l.handle && l.productId != null);
  if (!lookups.length) return [];

  return getProductJSON(lookups);
};

// ── Custom element ────────────────────────────────────────────────────────────

class B2BPlusQuickAdd extends HTMLElement {

  // ── Private fields ───────────────────────────────────────────────────────────
  /** @type {ShadowRoot} Closed shadow root — prevents outside DOM access. */
  #root = this.attachShadow({ mode: 'closed' });
  /** @type {ReturnType<typeof setTimeout>|null} */
  #searchDebounceTimer    = null;
  /** @type {AbortController|null} */
  #activeSearchController = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #panelCloseTimer        = null;
  /** @type {boolean} */
  #isAddingToCart         = false;
  /** Cache of handle → product JSON from the last search result set. */
  #searchResultsByHandle  = new Map();
  // DOM refs — set in #buildMarkup:
  /** @type {HTMLInputElement|null}  */ #searchInputEl  = null;
  /** @type {Element|null}           */ #clearButtonEl  = null;
  /** @type {Element|null}           */ #resultsPanelEl = null;
  /** @type {Element|null}           */ #resultsListEl  = null;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    if (this.dataset.b2bQuickAddInit === '1') return;
    this.dataset.b2bQuickAddInit = '1';

    this.#buildMarkup();
    this.#attachEventListeners();
  }

  disconnectedCallback() {
    this.#cancelPanelCloseTimer();
    this.#cancelActiveSearch();
    delete this.dataset.b2bQuickAddInit;
  }

  // ── Markup ────────────────────────────────────────────────────────────────

  #buildMarkup() {
    const { strings } = window.b2bPlus;
    const stylesheetUrl = this.dataset.stylesheet ?? '';

    render(quickAddShellTemplate(strings, stylesheetUrl), this.#root);

    this.#searchInputEl  = this.#root.querySelector('[data-quick-add-input]');
    this.#resultsListEl  = this.#root.querySelector('[data-quick-add-list]');
    this.#resultsPanelEl = this.#root.querySelector('[data-quick-add-results]');
    this.#clearButtonEl  = this.#root.querySelector('[data-quick-add-clear]');
  }

  // ── Event wiring ──────────────────────────────────────────────────────────

  #attachEventListeners() {
    this.#searchInputEl.addEventListener('input',   () => this.#onSearchInputChange());
    this.#searchInputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closeResultsPanel();
    });

    // Close the panel when the input loses focus — delayed so a result click
    // fires first. The mousedown handler below cancels this delay when the
    // user presses down on a result row.
    this.#searchInputEl.addEventListener('blur', () => {
      this.#panelCloseTimer = setTimeout(
        () => this.#closeResultsPanel(),
        RESULTS_PANEL_BLUR_CLOSE_DELAY_MS,
      );
    });

    this.#clearButtonEl.addEventListener('click', () => this.#clearSearch());

    // `mousedown.preventDefault()` stops the input from blurring when the
    // pointer lands on the results list, so the blur-close timer never starts.
    this.#resultsListEl.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.#cancelPanelCloseTimer();
    });

    this.#resultsListEl.addEventListener('click', (e) => {
      const resultRow = e.target.closest('[data-quick-add-result]');
      if (resultRow) this.#onResultRowSelected(resultRow);
    });

    this.#resultsListEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const resultRow = e.target.closest('[data-quick-add-result]');
        if (resultRow) this.#onResultRowSelected(resultRow);
      }
    });
  }

  // ── Search input ──────────────────────────────────────────────────────────

  #onSearchInputChange() {
    this.#updateClearButtonVisibility();
    const trimmedQuery = (this.#searchInputEl.value || '').trim();
    this.#searchInputEl.setAttribute('aria-expanded', 'false');

    this.#cancelSearchDebounceTimer();

    if (trimmedQuery.length < SEARCH_MIN_QUERY_LENGTH) {
      this.#closeResultsPanel();
      return;
    }

    this.#searchDebounceTimer = setTimeout(
      () => this.#runProductSearch(trimmedQuery),
      SEARCH_INPUT_DEBOUNCE_MS,
    );
  }

  #clearSearch() {
    this.#searchInputEl.value = '';
    this.#updateClearButtonVisibility();
    this.#closeResultsPanel();
    this.#searchInputEl.focus();
  }

  #updateClearButtonVisibility() {
    this.#clearButtonEl.hidden = (this.#searchInputEl.value || '').length === 0;
  }

  // ── Results panel visibility ──────────────────────────────────────────────

  #openResultsPanel() {
    this.#resultsPanelEl.hidden = false;
    this.#searchInputEl.setAttribute('aria-expanded', 'true');
  }

  #closeResultsPanel() {
    this.#cancelPanelCloseTimer();
    this.#resultsPanelEl.hidden = true;
    // Must use lit's `render(nothing, …)` here — not `replaceChildren()` — or the next
    // `render()` into this `<ul>` throws (insertBefore on null); lit keeps a Part on
    // the container and manual DOM clears desync it.
    if (this.#resultsListEl) {
      render(nothing, this.#resultsListEl);
    }
    this.#searchInputEl.setAttribute('aria-expanded', 'false');
    this.#searchResultsByHandle.clear();
  }

  // ── Timer helpers ─────────────────────────────────────────────────────────

  #cancelSearchDebounceTimer() {
    if (this.#searchDebounceTimer != null) {
      clearTimeout(this.#searchDebounceTimer);
      this.#searchDebounceTimer = null;
    }
  }

  #cancelPanelCloseTimer() {
    if (this.#panelCloseTimer != null) {
      clearTimeout(this.#panelCloseTimer);
      this.#panelCloseTimer = null;
    }
  }

  #cancelActiveSearch() {
    this.#cancelSearchDebounceTimer();
    if (this.#activeSearchController) {
      this.#activeSearchController.abort();
      this.#activeSearchController = null;
    }
  }

  // ── Storefront product search ─────────────────────────────────────────────

  /**
   * Fetch matching products (predictive search → catalog-price API) and render
   * the result rows.
   * @param {string} searchQuery
   */
  async #runProductSearch(searchQuery) {
    this.#cancelActiveSearch();
    this.#activeSearchController = new AbortController();
    const abortSignal = this.#activeSearchController.signal;

    const { strings } = window.b2bPlus;

    render(quickAddStatusRowTemplate(strings.quickAddSearching), this.#resultsListEl);
    this.#openResultsPanel();

    let fetchedProducts;
    try {
      fetchedProducts = await getProductJSONFromStorefrontSearch(
        searchQuery,
        { signal: abortSignal },
      );
    } catch (fetchError) {
      if (fetchError.name === 'AbortError') return;
      render(quickAddStatusRowTemplate(strings.quickAddError, true), this.#resultsListEl);
      return;
    } finally {
      this.#activeSearchController = null;
    }

    const validProducts = (Array.isArray(fetchedProducts) ? fetchedProducts : [])
      .filter((product) => product && product.handle);

    this.#searchResultsByHandle = new Map(
      validProducts.map((product) => [String(product.handle), product]),
    );

    if (!this.#searchResultsByHandle.size) {
      render(quickAddStatusRowTemplate(strings.quickAddNoResults), this.#resultsListEl);
      return;
    }

    const rowTemplates = [...this.#searchResultsByHandle.values()]
      .map((product) => quickAddProductRowTemplate(product, strings))
      .filter((t) => t !== nothing);

    render(
      rowTemplates.length
        ? html`${rowTemplates}`
        : quickAddStatusRowTemplate(strings.quickAddNoResults),
      this.#resultsListEl,
    );
  }

  // ── Add to cart ───────────────────────────────────────────────────────────

  /**
   * Called when the user clicks (or presses Enter on) a result row.
   * 1. Resolves full product JSON (from cache or network).
   * 2. Fetches B2B inventory.
   * 3. Ensures the first inventory combination shell exists in IndexedDB (no auto quantities).
   * 4. Dispatches `b2b:quick-add-added` so the cart prepends the new table.
   * @param {HTMLElement} resultRow - The `<li data-quick-add-result>` element.
   */
  async #onResultRowSelected(resultRow) {
    if (this.#isAddingToCart) return;

    const productHandle = resultRow.getAttribute('data-handle');
    if (!productHandle) return;

    this.#isAddingToCart = true;
    resultRow.setAttribute('aria-busy', 'true');

    const { strings } = window.b2bPlus;

    try {
      // The row's product always comes from the search result set (which already
      // holds catalog-priced JSON); a miss means the panel is stale, not a cache gap.
      const productJSON = this.#searchResultsByHandle.get(productHandle);

      if (!productJSON?.id) {
        toastNotification(strings.quickAddError, 'error');
        return;
      }

      if (await productHandleInCart(productHandle)) {
        this.#showInCartBadge(resultRow, strings.quickAddAlreadyInCart);
        return;
      }

      const inventoryResponse = await getInventory([productJSON.id]);
      const inventoryData = inventoryResponse?.success && inventoryResponse?.data
        ? inventoryResponse.data
        : null;

      if (!inventoryData) {
        toastNotification(strings.bulkAddInventoryError, 'error');
        return;
      }

      const addOutcome = await addFirstAvailableB2BLine(productJSON, inventoryData);

      if (addOutcome.ok) {
        toastNotification(strings.quickAddSuccess, 'success');
        this.#clearSearch();

        // addFirstAvailableB2BLine returns the updated cart lines directly,
        // built from the pre-write snapshot + the written item. Using them
        // avoids a getCart() re-read that could return stale data when
        // another tab wrote to IndexedDB concurrently.
        this.#dispatchProductAddedEvent(productJSON, inventoryData, addOutcome.cartLines ?? []);
      } else {
        toastNotification(strings.bulkAddNoInventory, 'error');
      }
    } catch (unexpectedError) {
      console.error('b2b-plus: quick add failed', unexpectedError);
      toastNotification(strings.quickAddError, 'error');
    } finally {
      this.#isAddingToCart = false;
      resultRow.removeAttribute('aria-busy');
    }
  }

  /**
   * Append an "already in cart" badge to the result row's meta section.
   * Idempotent — calling it twice on the same row has no effect.
   * @param {HTMLElement} resultRow
   * @param {string}      message
   */
  #showInCartBadge(resultRow, message) {
    if (resultRow.querySelector('.b2b-plus-quick-add__in-cart-badge')) return;
    const metaEl = resultRow.querySelector('.b2b-plus-quick-add__meta');
    if (!metaEl) return;
    const badge = document.createElement('span');
    badge.className = 'b2b-plus-quick-add__in-cart-badge';
    badge.textContent = message;
    metaEl.appendChild(badge);
  }

  /**
   * Dispatch `b2b:quick-add-added` on `<b2b-plus-cart>` so it can prepend
   * the new product table without reloading the entire cart list.
   *
   * The event carries the full product JSON, inventory data, and the updated
   * IndexedDB cart lines for this product so the cart component does not
   * need to re-fetch any data.
   *
   * @param {object}   productJSON
   * @param {object}   inventoryData
   * @param {object[]} updatedCartLines
   */
  #dispatchProductAddedEvent(productJSON, inventoryData, updatedCartLines) {
    const cartUpdateEvent = new CustomEvent('b2b:quick-add-added', {
      bubbles:  true,
      composed: true,
      detail:   { productJSON, inventoryData, cartItems: updatedCartLines },
    });

    // `b2b-plus-quick-add` may live inside `b2b-plus-cart`'s closed shadow root.
    // `getRootNode({ composed: false })` gives us that shadow root; `.host` is
    // `b2b-plus-cart` itself. `composed: true` on the event lets it cross any
    // further shadow boundaries on the way up.
    const outerRoot = this.getRootNode();
    const shadowHost = outerRoot instanceof ShadowRoot ? outerRoot.host : null;
    if (shadowHost?.matches?.('b2b-plus-cart')) {
      shadowHost.dispatchEvent(cartUpdateEvent);
      return;
    }

    // Fallback: element used outside a cart shadow root.
    document.querySelector('b2b-plus-cart')?.dispatchEvent(cartUpdateEvent);
  }
}

customElements.define('b2b-plus-quick-add', B2BPlusQuickAdd);

